import { ReactElement, useEffect } from 'react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Calculator, ErrorUtils, FormulaError, Range } from '@methodset/calculator-ts';
import { CoreUtils } from 'utils/CoreUtils';
import { DisplayType, ListWidgetConfiguration } from '@methodset/application-client-ts';
import { ValueDisplay } from 'components/ValueDisplay/ValueDisplay';
import { AlignType } from 'rc-table/lib/interface';
import classNames from 'classnames';
import './ListWidgetViewer.less';

export type ListWidgetViewerProps = {
    calculator: Calculator,
    configuration: ListWidgetConfiguration
}

export const ListWidgetViewer = (props: ListWidgetViewerProps): ReactElement => {

    useEffect(() => {
        const values = getLinkValues();
        if (values && values.length > 0) {
            handleLinkClicked(values[0][0]);
        }
    }, []);

    const handleLinkClicked = (value: any): void => {
        const variableId = props.configuration.rowLink?.variableId;
        if (!CoreUtils.isEmpty(variableId)) {
            const variables = props.calculator.variables;
            const variable = variables.get(variableId!, false);
            if (variable && variable.cell) {
                variable.cell.value = value;
            }
        }
    }

    const buildColumns = (): ColumnsType<any> => {
        const columnSets = props.configuration.columnSets;
        const columns: ColumnsType<any> = [];
        if (columnSets.length === 0) {
            return columns;
        }
        for (let i = 0; i < columnSets.length; i++) {
            const columnSet = columnSets[i];
            const columnDef = columnSet.columnDefs[0];
            const column = {
                key: i,
                title: columnSet.name,
                dataIndex: i.toString(),
                align: CoreUtils.toLower(columnDef.justification) as AlignType,
                className: "x-listwidgetviewer-cell"
            }
            columns.push(column);
        }
        return columns;
    }

    const buildData = (dataValues: any[][]): any[] | undefined => {
        const range = Range.fromUid(props.calculator, props.configuration.rangeId);
        const columnSets = props.configuration.columnSets;
        const matrix = [];
        for (let r = 0; r < dataValues.length; r++) {
            const row = dataValues[r];
            const data = { key: `row-${r}`, row: r } as any;
            let c = 0;
            for (const columnSet of columnSets) {
                const item = (
                    <div>
                        {columnSet.columnDefs.map(def => {
                            if (!def.rangeId) {
                                return;
                            }
                            const vector = Range.fromUid(props.calculator, def.rangeId);
                            const index = vector.upper.col - range.upper.col;
                            let value = row[index];
                            if (!CoreUtils.isEmpty(value)) {
                                if (ErrorUtils.isError(value)) {
                                    value = value.type;
                                } else {
                                    value = value.toString();
                                }
                                return (
                                    <div
                                        key={def.rangeId}
                                        className={
                                            classNames(
                                                "x-listwidgetviewer-item",
                                                { [`x-widgetviewer-size-${CoreUtils.toLower(def.size)}`]: !!def.size },
                                                { [`x-widgetviewer-color-${CoreUtils.toLower(def.color)}`]: !!def.color },
                                                { [`x-widgetviewer-weight-${CoreUtils.toLower(def.weight)}`]: !!def.weight },
                                                { [`x-widgetviewer-justification-${CoreUtils.toLower(def.justification)}`]: !!def.justification }
                                            )
                                        }>
                                        {(!def.display || def.display === DisplayType.NORMAL) &&
                                            <>{value}</>
                                        }
                                        {def.display && def.display !== DisplayType.NORMAL &&
                                            <ValueDisplay type="sign" value={value} />
                                        }
                                    </div>
                                )
                            }
                        })}
                    </div>
                );
                data[c++] = item;
            }
            matrix.push(data);
        }
        return isEmpty(matrix, dataValues) ? undefined : matrix;
    }

    const isEmpty = (matrix: any[][], values: any[][]): boolean => {
        if (matrix.length === 1) {
            const columnSets = props.configuration.columnSets;
            for (let i = 0; i < columnSets.length; i++) {
                const columnSet = columnSets[i];
                const columnDefs = columnSet.columnDefs;
                for (let j = 0; j < columnDefs.length; j++) {
                    const columnDef = columnDefs[j];
                    const rangeId = columnDef.rangeId;
                    if (!rangeId) {
                        return true;
                    }
                    const range = Range.fromUid(props.calculator, rangeId);
                    const col = range.upper.col;
                    const value = values[0][col];
                    // const index = columnDef.index;
                    // const value = values[0][index - 1];
                    if (!FormulaError.isError(value) && value !== undefined) {
                        // If the root cell has an error and the rest of the matrix 
                        // could not be generated, then there are no table contents.
                        return false;
                    }
                }
            }
            return true;
        } else {
            return false;
        }
    }

    const buildRange = (rangeId: string | undefined): Range | undefined => {
        try {
            return rangeId ? Range.fromUid(props.calculator, rangeId) : undefined;
        } catch (e) {
            return undefined;
        }
    }

    const getLinkValues = (): any[][] | undefined => {
        const rowLink = props.configuration.rowLink;
        if (rowLink && rowLink.rangeId) {
            const range = buildRange(rowLink.rangeId);
            return range ? range.values(false) : undefined;
        } else {
            return undefined;
        }
    }

    const getDataValues = (): any[][] | undefined => {
        const rangeId = props.configuration.rangeId
        const range = buildRange(rangeId);
        return range ? range.values(true) : undefined;
    }

    const handleRowClick = (linkValues: any[][], index: number): void => {
        if (index < 0 || index >= linkValues.length) {
            return;
        }
        const value = linkValues[index][0];
        handleLinkClicked(value);
    }

    const buildView = (): ReactElement => {
        let dataValues: any[][] | undefined;
        let linkValues: any[][] | undefined;
        try {
            dataValues = getDataValues();
            linkValues = getLinkValues();
        } catch (e) {
            // Invalid data sheet or range - skip.
            return <>Error</>;
        }
        // Add a clickable row if value range is specified.
        const onClickableRow = (record: any, index?: number) => {
            return {
                style: { cursor: "pointer" },
                onClick: () => handleRowClick(linkValues!, index!)
            }
        }
        return (
            <div>
                <Table
                    pagination={false}
                    size="small"
                    columns={buildColumns()}
                    onRow={linkValues ? onClickableRow : undefined}
                    dataSource={buildData(dataValues!)}
                    showHeader={!!props.configuration.hasHeaders}
                />
            </div>
        );
    }

    return (
        <div className="x-listwidgetviewer">
            {buildView()}
        </div>
    );
}
